import React, { useState, useRef, useEffect } from 'react';
import { uploadService, settingsService } from '../api/services';
import './FileUpload.css';

const FileUpload = ({ attachments = [], onAttachmentsChange }) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [maxSizeMb, setMaxSizeMb] = useState(10);
  const fileInputRef = useRef(null);

  useEffect(() => {
    settingsService.getAll().then(res => {
      const val = parseInt(res.data?.max_file_size_mb);
      if (val > 0) setMaxSizeMb(val);
    }).catch(() => {});
  }, []);

  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;
    setError('');
    setUploading(true);

    try {
      // Check file sizes client-side
      for (const file of files) {
        if (file.size > maxSizeMb * 1024 * 1024) {
          setError(`"${file.name}" exceeds max size of ${maxSizeMb}MB`);
          setUploading(false);
          return;
        }
      }

      const formData = new FormData();
      for (const file of files) {
        formData.append('files', file);
      }

      const res = await uploadService.upload(formData);
      const newAttachments = res.data.map(a => ({
        ...a,
        url: uploadService.getFileUrl(a.file_path),
      }));
      onAttachmentsChange([...attachments, ...newAttachments]);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFiles(Array.from(e.dataTransfer.files));
  };

  const handleRemove = async (id) => {
    try {
      await uploadService.delete(id);
      onAttachmentsChange(attachments.filter(a => a.id !== id));
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const isImageAttachment = (attachment) => {
    const imageTypes = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'];
    return attachment.mime_type?.startsWith('image/') || imageTypes.includes(String(attachment.file_type).toLowerCase());
  };

  const getIcon = (type) => {
    const normalized = String(type || '').toLowerCase();
    const icons = { java: '☕', js: '💻', jsx: '💻', ts: '💻', tsx: '💻', py: '🐍', pdf: '📄', zip: '📦', rar: '📦' };
    return icons[normalized] || '📁';
  };

  return (
    <div className="file-upload">
      <div
        className={`upload-dropzone ${dragOver ? 'drag-over' : ''} ${uploading ? 'uploading' : ''}`}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          style={{ display: 'none' }}
          onChange={(e) => handleFiles(Array.from(e.target.files))}
        />
        {uploading ? (
          <span className="upload-text">⏳ 上傳中...</span>
        ) : (
          <span className="upload-text">
            📎 拖拉檔案到這裡，或<span className="upload-link">點擊選擇檔案</span>
            <small className="upload-hint">單檔上限 {maxSizeMb}MB</small>
          </span>
        )}
      </div>

      {error && <div className="upload-error">⚠️ {error}</div>}

      {attachments.length > 0 && (
        <div className="upload-list">
          {attachments.map((a) => (
            <div key={a.id} className="upload-item">
              <span className="upload-item-icon">{getIcon(a.file_type)}</span>
              {isImageAttachment(a) && a.url && (
                <img src={a.url} alt={a.original_name} className="upload-thumb" />
              )}
              <span className="upload-item-name">{a.original_name}</span>
              <span className="upload-item-size">{formatSize(a.file_size)}</span>
              <button className="upload-item-remove" onClick={(e) => { e.stopPropagation(); handleRemove(a.id); }} title="移除">✕</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default FileUpload;
